import { useEffect } from "react";

const ROOT_CANONICAL = "https://signalgpt.io/";
const ROOT_DESCRIPTION =
  "Signal GPT delivers AI-powered stock buy signals, real-time market intelligence, and portfolio risk analysis for self-directed investors.";
const ROOT_TITLE = "Signal GPT - AI-Powered Stock Recommendations";

function getOrCreateMeta(selector: string, create: () => HTMLMetaElement): HTMLMetaElement {
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

/**
 * Sets <link rel="canonical">, <meta name="description">, and Open Graph
 * tags (og:url, og:title, og:description, twitter:title, twitter:description)
 * for the current route, restoring previous values on unmount.
 */
export function useCanonical(path: string, description?: string, title?: string) {
  useEffect(() => {
    const url = path.startsWith("http") ? path : `https://signalgpt.io${path}`;

    // Canonical
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    const previousHref = link.getAttribute("href");
    link.setAttribute("href", url);

    // Description
    const descMeta = getOrCreateMeta('meta[name="description"]', () => {
      const m = document.createElement("meta");
      m.setAttribute("name", "description");
      return m;
    });
    const previousDesc = descMeta.getAttribute("content");
    if (description) {
      descMeta.setAttribute("content", description);
    }

    // OG URL
    const ogUrl = getOrCreateMeta('meta[property="og:url"]', () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:url");
      return m;
    });
    const previousOgUrl = ogUrl.getAttribute("content");
    ogUrl.setAttribute("content", url);

    // OG Title
    const ogTitle = getOrCreateMeta('meta[property="og:title"]', () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:title");
      return m;
    });
    const previousOgTitle = ogTitle.getAttribute("content");
    const ogTitleValue = title || document.title || ROOT_TITLE;
    ogTitle.setAttribute("content", ogTitleValue);

    // OG Description
    const ogDesc = getOrCreateMeta('meta[property="og:description"]', () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:description");
      return m;
    });
    const previousOgDesc = ogDesc.getAttribute("content");
    if (description) {
      ogDesc.setAttribute("content", description);
    }

    // Twitter Title
    const twTitle = getOrCreateMeta('meta[name="twitter:title"]', () => {
      const m = document.createElement("meta");
      m.setAttribute("name", "twitter:title");
      return m;
    });
    const previousTwTitle = twTitle.getAttribute("content");
    twTitle.setAttribute("content", ogTitleValue);

    // Twitter Description
    const twDesc = getOrCreateMeta('meta[name="twitter:description"]', () => {
      const m = document.createElement("meta");
      m.setAttribute("name", "twitter:description");
      return m;
    });
    const previousTwDesc = twDesc.getAttribute("content");
    if (description) {
      twDesc.setAttribute("content", description);
    }

    return () => {
      if (link) link.setAttribute("href", previousHref || ROOT_CANONICAL);
      if (description && descMeta) descMeta.setAttribute("content", previousDesc || ROOT_DESCRIPTION);
      ogUrl.setAttribute("content", previousOgUrl || ROOT_CANONICAL);
      ogTitle.setAttribute("content", previousOgTitle || ROOT_TITLE);
      if (description && ogDesc) ogDesc.setAttribute("content", previousOgDesc || ROOT_DESCRIPTION);
      twTitle.setAttribute("content", previousTwTitle || ROOT_TITLE);
      if (description && twDesc) twDesc.setAttribute("content", previousTwDesc || ROOT_DESCRIPTION);
    };
  }, [path, description, title]);
}
